import React, { useMemo, useEffect } from 'react'
import { CrossSection, SectionMaterialStrength } from '../types'
import { ShearReinforcementSummary } from '../shearReinforcement'
import { calculateElasticModulus, formatInertia, formatSectionModulus } from '../utils/sectionProperties'
import { useTranslation, TranslationKey } from '@structura/i18n'

type TFunc = ReturnType<typeof useTranslation>['t']

// Steel section class (1–4) → its short translation key.
const SECTION_CLASS_SHORT_KEY: Record<number, TranslationKey> = {
  1: 'beam.section.classShort1',
  2: 'beam.section.classShort2',
  3: 'beam.section.classShort3',
  4: 'beam.section.classShort4'
}

interface CrossSectionDetailsTableProps {
  crossSection: CrossSection
  material: string
  timberKind?: 'glulam' | 'solid'
  materialStrength?: SectionMaterialStrength | null
  shearReinforcement?: ShearReinforcementSummary | null
  onMomentCapacitiesCalculated?: (capacities: {
    elasticMomentCapacityKNm?: number
    plasticMomentCapacityKNm?: number
    reinforcedMomentCapacityKNm?: number
  }) => void
  onSectionPropertiesCalculated?: (properties: {
    elasticModulusY?: number
    plasticModulusY?: number
    effectiveDepth?: number
  }) => void
}

type MaterialCategory = 'steel' | 'concrete' | 'timber' | 'other'

function formatMillimeter(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }

  const roundedInteger = Math.round(value)
  if (Math.abs(value - roundedInteger) < 1e-6) {
    return `${roundedInteger} mm`
  }

  return `${value.toFixed(1)} mm`
}

// steelYieldStrength is the characteristic fy in Pa — required so ε = √(235/fy) matches the
// actual grade. Assuming S355 here previously showed non-conservative classes for S420/S460.
function classifySteelSection(crossSection: CrossSection, steelYieldStrength: number): number | null {
  const width = crossSection.width
  const height = crossSection.height
  const webThickness = crossSection.webThickness
  const flangeThickness = crossSection.flangeThickness

  if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(webThickness) || !Number.isFinite(flangeThickness)) {
    return null
  }

  const effectiveWebThickness = webThickness as number
  const effectiveFlangeThickness = flangeThickness as number

  if (effectiveWebThickness <= 0 || effectiveFlangeThickness <= 0) {
    return null
  }

  const epsilon = Math.sqrt(235e6 / Math.max(steelYieldStrength, 1))

  const flangeOutstand = (width - effectiveWebThickness) / 2
  const webDepth = height - 2 * effectiveFlangeThickness

  if (!Number.isFinite(flangeOutstand) || flangeOutstand <= 0 || !Number.isFinite(webDepth) || webDepth <= 0) {
    return null
  }

  const flangeRatio = flangeOutstand / effectiveFlangeThickness
  const webRatio = webDepth / effectiveWebThickness

  function classifyRatio(ratio: number, limit1: number, limit2: number, limit3: number): number {
    if (!Number.isFinite(ratio) || ratio <= 0) {
      return 1
    }
    if (ratio <= limit1) {
      return 1
    }
    if (ratio <= limit2) {
      return 2
    }
    if (ratio <= limit3) {
      return 3
    }
    return 4
  }

  const flangeClass = classifyRatio(flangeRatio, 9 * epsilon, 10 * epsilon, 14 * epsilon)
  const webClass = classifyRatio(webRatio, 72 * epsilon, 83 * epsilon, 124 * epsilon)

  return Math.max(flangeClass, webClass)
}

function describeSteelSectionClass(sectionClass: number, t: TFunc): string {
  const key = SECTION_CLASS_SHORT_KEY[sectionClass]
  return key ? t(key) : t('beam.section.classShortN', { n: sectionClass })
}

const DEFAULT_STRENGTHS: Record<MaterialCategory, { designStrength: number; label: string } | null> = {
  steel: { designStrength: 355e6, label: 'Steel S355 (fy / γM0)' },
  concrete: { designStrength: 17e6, label: 'Concrete C30/37 (fcd)' },
  timber: { designStrength: 19.2e6, label: 'Timber GL24h (fb,d)' },
  other: null
}

function getDefaultStrength(category: MaterialCategory): { designStrength: number; label: string } | null {
  return DEFAULT_STRENGTHS[category] ?? null
}

function formatMomentCapacity(momentNm: number | null | undefined): string {
  if (momentNm === undefined || momentNm === null || !Number.isFinite(momentNm)) {
    return '—'
  }
  const momentkNm = momentNm / 1000
  if (!Number.isFinite(momentkNm)) {
    return '—'
  }
  return `${momentkNm.toFixed(1)} kNm`
}

const CrossSectionDetailsTable: React.FC<CrossSectionDetailsTableProps> = ({ crossSection, material, timberKind, materialStrength, shearReinforcement, onMomentCapacitiesCalculated, onSectionPropertiesCalculated }) => {
  const { t } = useTranslation()
  const materialCategory = useMemo<MaterialCategory>(() => {
    const label = (material || crossSection.material || '').toLowerCase()
    if (label === 'steel') {
      return 'steel'
    }
    if (label === 'concrete') {
      return 'concrete'
    }
    if (label === 'timber' || label === 'wood' || label === 'glulam' || label === 'solid') {
      return 'timber'
    }
    return 'other'
  }, [material, crossSection.material])

  // Material name shown in the section table: the chosen grade/class
  // (e.g. "S355 J0", "C30/37", "GL30c"). For timber the localized product
  // type is prepended (e.g. "Limtre GL30c").
  const materialLabel = useMemo<string>(() => {
    const name = materialStrength?.materialName
    if (materialCategory === 'timber') {
      const kindLabel = timberKind ? t(timberKind === 'glulam' ? 'beam.timberType.glulam' : 'beam.timberType.solid') : null
      if (kindLabel && name) {
        return `${kindLabel} ${name}`
      }
      return kindLabel ?? name ?? crossSection.material ?? material ?? '—'
    }
    return name ?? crossSection.material ?? material ?? '—'
  }, [materialCategory, timberKind, materialStrength, crossSection.material, material, t])

  // Secondary line under the material name: design strengths and ultimate
  // strain where defined for the material (f_cd/f_yd/f_md, rebar f_sd, ε_u).
  const materialDetailLine = useMemo<string | null>(() => {
    if (!materialStrength) {
      return null
    }
    const strengthSymbol = materialCategory === 'steel' ? 'fyd' : materialCategory === 'concrete' ? 'fcd' : materialCategory === 'timber' ? 'fmd' : 'fd'
    const parts: string[] = []
    if (Number.isFinite(materialStrength.designStrength)) {
      parts.push(`${strengthSymbol} = ${(materialStrength.designStrength / 1e6).toFixed(1)} MPa`)
    }
    if (materialStrength.concreteUltimateStrain !== undefined && Number.isFinite(materialStrength.concreteUltimateStrain)) {
      parts.push(`εcu = ${(materialStrength.concreteUltimateStrain * 1000).toFixed(1)} ‰`)
    }
    if (materialStrength.reinforcementDesignStrength !== undefined && Number.isFinite(materialStrength.reinforcementDesignStrength)) {
      parts.push(`fsd = ${(materialStrength.reinforcementDesignStrength / 1e6).toFixed(0)} MPa`)
    }
    if (materialStrength.rebarDesignStrain !== undefined && Number.isFinite(materialStrength.rebarDesignStrain)) {
      parts.push(`εud = ${(materialStrength.rebarDesignStrain * 100).toFixed(1)} %`)
    }
    return parts.length > 0 ? parts.join(' · ') : null
  }, [materialStrength, materialCategory])

  const areaDisplay = useMemo<string>(() => {
    if (crossSection.area === undefined || !Number.isFinite(crossSection.area)) {
      return '—'
    }
    const areaCm2 = crossSection.area * 1e4
    return `${areaCm2.toFixed(2)} cm²`
  }, [crossSection.area])

  const elasticModulusY = useMemo<number | undefined>(() => {
    if (crossSection.elasticModulusY !== undefined && Number.isFinite(crossSection.elasticModulusY)) {
      return crossSection.elasticModulusY
    }
    if (Number.isFinite(crossSection.inertia) && Number.isFinite(crossSection.height)) {
      return calculateElasticModulus(crossSection.inertia, crossSection.height)
    }
    return undefined
  }, [crossSection.elasticModulusY, crossSection.inertia, crossSection.height])

  // For concrete: calculate effective depth if not stored
  const effectiveDepth = useMemo<number | undefined>(() => {
    if (materialCategory !== 'concrete') {
      return undefined
    }
    if (crossSection.effectiveDepth !== undefined && Number.isFinite(crossSection.effectiveDepth)) {
      return crossSection.effectiveDepth
    }
    const cover = crossSection.effectiveCover || 40
    return crossSection.height - cover
  }, [materialCategory, crossSection.effectiveDepth, crossSection.effectiveCover, crossSection.height])

  const autoSectionClass = useMemo<number | null>(() => {
    if (materialCategory !== 'steel') {
      return null
    }

    if (crossSection.sectionType && !['i-beam', 'h-beam', 'i-beam', 'rectangular', 'circular', 't-beam', 'l-beam'].includes(crossSection.sectionType.toLowerCase())) {
      // For non-standard shapes we currently do not calculate automatic class
      return null
    }

    // No resolved fy → no client-side class preview (the backend's Section Classification check
    // is the authoritative verdict). Guessing a grade here would show a wrong class.
    const yieldStrengthPa = materialStrength?.characteristicYieldStrengthPa
    if (yieldStrengthPa === undefined || !Number.isFinite(yieldStrengthPa) || yieldStrengthPa <= 0) {
      return null
    }

    return classifySteelSection(crossSection, yieldStrengthPa)
  }, [materialCategory, crossSection, materialStrength])

  const sectionClassLabel = useMemo<string>(() => {
    const sectionClass = crossSection.sectionClass
    if (sectionClass === undefined || sectionClass === null || sectionClass === 'auto') {
      if (materialCategory === 'steel' && autoSectionClass) {
        return `${describeSteelSectionClass(autoSectionClass, t)}${t('beam.section.autoSuffix')}`
      }
      return t('beam.section.classAutoGeometry')
    }

    const numericClass = Number.parseInt(sectionClass, 10)
    return describeSteelSectionClass(numericClass, t)
  }, [crossSection.sectionClass, materialCategory, autoSectionClass, t])

  const reinforcementRatioDisplay = useMemo<string>(() => {
    if (crossSection.reinforcementRatio === undefined || !Number.isFinite(crossSection.reinforcementRatio)) {
      return '—'
    }
    return `${(crossSection.reinforcementRatio * 100).toFixed(2)}%`
  }, [crossSection.reinforcementRatio])

  const strengthInfo = useMemo(() => {
    if (materialStrength && Number.isFinite(materialStrength.designStrength)) {
      return {
        designStrength: materialStrength.designStrength,
        label: materialStrength.label ?? null,
        reinforcementDesignStrength: materialStrength.reinforcementDesignStrength,
        reinforcementLabel: materialStrength.reinforcementLabel ?? null
      }
    }
    const defaults = getDefaultStrength(materialCategory)
    if (!defaults) {
      return null
    }
    return {
      designStrength: defaults.designStrength,
      label: defaults.label,
      reinforcementDesignStrength: undefined,
      reinforcementLabel: undefined
    }
  }, [materialStrength, materialCategory])

  const elasticMomentNm = useMemo<number | null>(() => {
    if (!strengthInfo) {
      return null
    }
    const designStrength = strengthInfo.designStrength
    const modulus = elasticModulusY ?? crossSection.elasticModulusY
    if (!Number.isFinite(designStrength) || designStrength <= 0 || !Number.isFinite(modulus) || (modulus as number) <= 0) {
      return null
    }
    return designStrength * (modulus as number)
  }, [strengthInfo, elasticModulusY, crossSection.elasticModulusY])

  const plasticMomentNm = useMemo<number | null>(() => {
    if (materialCategory !== 'steel' || !strengthInfo) {
      return null
    }
    const designStrength = strengthInfo.designStrength
    const plasticModulus = crossSection.plasticModulusY ?? elasticModulusY ?? crossSection.elasticModulusY
    if (!Number.isFinite(designStrength) || designStrength <= 0 || !Number.isFinite(plasticModulus) || (plasticModulus as number) <= 0) {
      return null
    }
    return designStrength * (plasticModulus as number)
  }, [materialCategory, strengthInfo, crossSection.plasticModulusY, elasticModulusY, crossSection.elasticModulusY])

  // Simplified concrete moment capacity based on rectangular stress block
  // Fcd = 0.5 × b × h × fcd (triangular stress distribution)
  // Mrd = min(Fcd, Fsd) × (z + h/3)
  const concreteMomentNm = useMemo<number | null>(() => {
    if (materialCategory !== 'concrete' || !strengthInfo) {
      return null
    }
    const fcd = strengthInfo.designStrength // Pa
    const fyd = strengthInfo.reinforcementDesignStrength // Pa
    const b = crossSection.width / 1000 // m
    const h = crossSection.height / 1000 // m
    const As = crossSection.reinforcementAreaBottom // m²
    const z = crossSection.reinforcementLeverArmBottom // m

    if (!Number.isFinite(fcd) || fcd <= 0) return null
    if (!Number.isFinite(fyd) || fyd <= 0) return null
    if (!Number.isFinite(As) || As <= 0) return null
    if (!Number.isFinite(z) || z <= 0) return null

    // Concrete compression capacity (triangular stress block)
    const Fcd = 0.5 * b * h * fcd // N

    // Steel tension capacity
    const Fsd = fyd * As // N

    // Moment capacity = min(Fcd, Fsd) × (z + h/3)
    const F = Math.min(Fcd, Fsd) // N
    const leverArm = z + h / 3 // m
    const Mrd = F * leverArm // Nm

    return Mrd
  }, [materialCategory, strengthInfo, crossSection.width, crossSection.height, crossSection.reinforcementAreaBottom, crossSection.reinforcementLeverArmBottom])

  const reinforcementMomentNm = useMemo<number | null>(() => {
    if (materialCategory !== 'concrete' || !strengthInfo) {
      return null
    }
    const reinforcementStrength = strengthInfo.reinforcementDesignStrength
    const area = crossSection.reinforcementAreaBottom
    const leverArm = crossSection.reinforcementLeverArmBottom
    if (!Number.isFinite(reinforcementStrength) || (reinforcementStrength as number) <= 0) {
      return null
    }
    if (!Number.isFinite(area) || (area as number) <= 0) {
      return null
    }
    if (!Number.isFinite(leverArm) || (leverArm as number) <= 0) {
      return null
    }
    return (reinforcementStrength as number) * (area as number) * (leverArm as number)
  }, [materialCategory, strengthInfo, crossSection.reinforcementAreaBottom, crossSection.reinforcementLeverArmBottom])

  // Convert from Nm to kNm and notify parent component when MRd values change
  useEffect(() => {
    if (onMomentCapacitiesCalculated) {
      onMomentCapacitiesCalculated({
        elasticMomentCapacityKNm: elasticMomentNm !== null ? elasticMomentNm / 1000 : undefined,
        plasticMomentCapacityKNm: plasticMomentNm !== null ? plasticMomentNm / 1000 : undefined,
        reinforcedMomentCapacityKNm: reinforcementMomentNm !== null ? reinforcementMomentNm / 1000 : undefined
      })
    }
  }, [elasticMomentNm, plasticMomentNm, reinforcementMomentNm, onMomentCapacitiesCalculated])

  // Notify parent component about calculated section properties
  useEffect(() => {
    if (onSectionPropertiesCalculated) {
      onSectionPropertiesCalculated({
        elasticModulusY: elasticModulusY,
        plasticModulusY: crossSection.plasticModulusY,
        effectiveDepth: effectiveDepth
      })
    }
  }, [elasticModulusY, crossSection.plasticModulusY, effectiveDepth, onSectionPropertiesCalculated])

  const elasticMomentDisplay = useMemo(() => formatMomentCapacity(elasticMomentNm), [elasticMomentNm])
  const plasticMomentDisplay = useMemo(() => formatMomentCapacity(plasticMomentNm), [plasticMomentNm])
  const concreteMomentDisplay = useMemo(() => formatMomentCapacity(concreteMomentNm), [concreteMomentNm])
  const momentCapacityLabel = strengthInfo?.label ?? null

  return (
    <table className="table table-sm">
      <tbody>
        <tr>
          <td><strong>{t('beam.section.section')}</strong></td>
          <td>{crossSection.name || '—'}</td>
        </tr>
        <tr>
          <td><strong>{t('beam.section.material')}</strong></td>
          <td>
            {materialLabel}
            {materialDetailLine && (
              <div className="text-muted" style={{ fontSize: '0.8em' }}>{materialDetailLine}</div>
            )}
          </td>
        </tr>
        <tr>
          <td><strong>{t('beam.section.width')}</strong></td>
          <td>{formatMillimeter(crossSection.width)}</td>
        </tr>
        <tr>
          <td><strong>{t('beam.section.height')}</strong></td>
          <td>{formatMillimeter(crossSection.height)}</td>
        </tr>
        <tr>
          <td><strong>{t('beam.section.area')}</strong></td>
          <td>{areaDisplay}</td>
        </tr>
        <tr>
          <td><strong>{t('beam.section.inertia')} I<sub>y</sub>:</strong></td>
          <td>{formatInertia(crossSection.inertia)}</td>
        </tr>
        {materialCategory === 'steel' ? (
          <>
            <tr>
              <td><strong>{t('beam.section.approx')} M<sub>Rd,el</sub>:</strong></td>
              <td>
                {elasticMomentDisplay}
                {momentCapacityLabel && (
                  <div className="text-muted small">{momentCapacityLabel}</div>
                )}
              </td>
            </tr>
            <tr>
              <td><strong>{t('beam.section.approx')} M<sub>Rd,pl</sub>:</strong></td>
              <td>
                {plasticMomentDisplay}
                {momentCapacityLabel && plasticMomentNm === null && (
                  <div className="text-muted small">{t('beam.section.plasticModulusUnavailable')}</div>
                )}
              </td>
            </tr>
          </>
        ) : materialCategory === 'timber' ? (
          <tr>
            <td><strong>{t('beam.section.approx')} M<sub>Rd</sub>:</strong></td>
            <td>
              {elasticMomentDisplay}
              {momentCapacityLabel && (
                <div className="text-muted small">{momentCapacityLabel}</div>
              )}
            </td>
          </tr>
        ) : null}
        {materialCategory !== 'concrete' && (
          <tr>
            <td><strong>W<sub>el,y</sub>:</strong></td>
            <td>{formatSectionModulus(elasticModulusY)}</td>
          </tr>
        )}
        {(materialCategory === 'steel' || crossSection.plasticModulusY !== undefined) && (
          <tr>
            <td><strong>W<sub>pl,y</sub>:</strong></td>
            <td>{formatSectionModulus(crossSection.plasticModulusY)}</td>
          </tr>
        )}
        {materialCategory === 'steel' && (
          <>
            <tr>
              <td><strong>{t('beam.section.webT')}</strong></td>
              <td>{formatMillimeter(crossSection.webThickness)}</td>
            </tr>
            <tr>
              <td><strong>{t('beam.section.flangeT')}</strong></td>
              <td>{formatMillimeter(crossSection.flangeThickness)}</td>
            </tr>
            <tr>
              <td><strong>{t('beam.section.sectionClass')}</strong></td>
              <td>{sectionClassLabel}</td>
            </tr>
          </>
        )}
        <tr>
          <td><strong>{t('beam.section.shearFactor')}</strong></td>
          <td>
            {crossSection.shearCorrectionFactor !== undefined && Number.isFinite(crossSection.shearCorrectionFactor)
              ? crossSection.shearCorrectionFactor.toFixed(3)
              : (5/6).toFixed(3)}
            <span className="text-muted small ms-1">(Timoshenko)</span>
          </td>
        </tr>
        {materialCategory === 'concrete' && (
          <>
            <tr>
              <td><strong>A<sub>s</sub> ({t('beam.section.reinforcement')}):</strong></td>
              <td>{crossSection.reinforcementAreaBottom ? `${(crossSection.reinforcementAreaBottom * 1e6).toFixed(0)} mm²` : '—'}</td>
            </tr>
            <tr>
              <td><strong>d ({t('beam.section.effectiveDepth')}):</strong></td>
              <td>{effectiveDepth ? `${effectiveDepth.toFixed(0)} mm` : '—'}</td>
            </tr>
            <tr>
              <td><strong>ρ ({t('beam.section.reinforcementRatio')}):</strong></td>
              <td>{reinforcementRatioDisplay}</td>
            </tr>
            <tr>
              <td><strong>A<sub>sw</sub> ({t('beam.section.shearReinforcement')}):</strong></td>
              <td>
                {shearReinforcement
                  ? <>{`${shearReinforcement.aswMm2PerM.toFixed(0)} mm²/m`}<span className="text-muted small ms-1">{`(Ø${shearReinforcement.diameter} mm c/c ${shearReinforcement.spacing} mm, ${shearReinforcement.legs}-leg)`}</span></>
                  : '—'}
              </td>
            </tr>
            <tr>
              <td><strong>{t('beam.section.approx')} M<sub>Rd</sub>:</strong></td>
              <td>
                {concreteMomentDisplay}
                {momentCapacityLabel && (
                  <div className="text-muted small">{momentCapacityLabel}</div>
                )}
              </td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  )
}

export default CrossSectionDetailsTable


